/**
 * Pure logica achter het verzendingenoverzicht (BMX-20): zoeken en de korte weergave van een
 * track & trace. Geen Firestore- of React-afhankelijkheden, zodat het los te testen is (zie
 * shipmentList.test.js).
 */
import { normalizeContainerNumber } from "./containerNumber";

const isUrl = (value) => /^https?:\/\//i.test(value || "");

/**
 * Hoe een track & trace in de tabel getoond wordt. Leveranciers vullen het veld met wat ze
 * hebben: meestal de volledige URL van de rederij (~110 tekens), soms alleen een code.
 *
 * Een URL wordt een link met alleen de domeinnaam als tekst ("maersk.com"): kort genoeg voor een
 * tabelkolom, en je ziet nog welke vervoerder het is. Lukt het niet om er een domein uit te halen,
 * dan valt de tekst terug op "Openen". Een code die geen URL is, blijft tekst — de tabel kapt die
 * visueel af, de volledige waarde staat in de tooltip.
 *
 * @param {*} trackingCode - ruwe waarde uit `purchaseOrderShipments.trackingCode`
 * @returns {{ url: string|null, label: string }|null} null als er niets is ingevuld
 */
export const trackingDisplay = (trackingCode) => {
  const value = String(trackingCode ?? "").trim();
  if (!value) return null;
  if (!isUrl(value)) return { url: null, label: value };
  try {
    const host = new URL(value).hostname.replace(/^www\./i, "");
    return { url: value, label: host || "Openen" };
  } catch (e) {
    return { url: value, label: "Openen" };
  }
};

/**
 * Past een verzending bij de zoekterm? Er wordt gezocht op zendingnummer, containernummer, track
 * & trace, leverancier en de gekoppelde ordernummers.
 *
 * Containernummers worden daarnaast genormaliseerd vergeleken (zelfde regel als BMX-21): wie
 * "msku 123" typt, vindt ook "MSKU1234567", en andersom. Zonder die tweede vergelijking zou een
 * nummer dat als "MSKU 123-456" is ingevoerd niet te vinden zijn op "MSKU123456".
 */
export const matchesShipmentSearch = (shipment, search) => {
  const needle = String(search ?? "").trim().toLowerCase();
  if (!needle) return true;
  const haystack = [
    shipment.shipmentNumber,
    shipment.containerNumber,
    shipment.trackingCode,
    shipment.supplierName,
    ...(shipment.sourceOrders || []).map((so) => so.purchaseOrderNumber),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  if (haystack.includes(needle)) return true;

  const containerNeedle = normalizeContainerNumber(search);
  return containerNeedle !== "" && normalizeContainerNumber(shipment.containerNumber).includes(containerNeedle);
};
